import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  StyleSheet,
  ActivityIndicator,
  View,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  TextInput,
} from "react-native";
import { useRouter, useLocalSearchParams } from "expo-router";
import { ThemedText } from "@/components/ThemedText";
import { Button } from "@/components/ui/Button";
import { useTheme } from "@/hooks/useTheme";
import { supabase } from "@/lib/supabase-client";
import { Image, ImageSource } from "expo-image";
import { MaterialIcons } from "@expo/vector-icons";
import Head from "expo-router/head";

const OTP_LENGTH = 6;
const RESEND_COOLDOWN = 60;

export default function VerifyOtp() {
  const router = useRouter();
  const { email } = useLocalSearchParams<{ email: string }>();
  const { colors } = useTheme();

  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [logo] = useState<ImageSource>(require("@/assets/images/rockforce-logo-rf-yellow.svg"));

  const inputRefs = useRef<(TextInput | null)[]>([]);

  // Guard: redirect if no email param
  useEffect(() => {
    if (!email) {
      router.replace("/forgot-password");
    }
  }, [email, router]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  // Auto-focus first input on mount
  useEffect(() => {
    setTimeout(() => inputRefs.current[0]?.focus(), 100);
  }, []);

  const submitOtp = useCallback(
    async (digits: string[]) => {
      const token = digits.join("");
      if (token.length !== OTP_LENGTH || !email) return;

      setVerifying(true);
      setError(null);

      try {
        const { error: verifyError } = await supabase.auth.verifyOtp({
          email,
          token,
          type: "recovery",
        });

        if (verifyError) {
          setError("Invalid or expired code. Please try again.");
          setOtp(Array(OTP_LENGTH).fill(""));
          setTimeout(() => inputRefs.current[0]?.focus(), 100);
        } else {
          router.replace("/update-password?recovery=true");
        }
      } catch {
        setError("Something went wrong. Please try again.");
        setOtp(Array(OTP_LENGTH).fill(""));
        setTimeout(() => inputRefs.current[0]?.focus(), 100);
      } finally {
        setVerifying(false);
      }
    },
    [email, router],
  );

  const handleChange = (text: string, index: number) => {
    const digits = text.replace(/\D/g, "");

    // Multi-character paste came through onChangeText — fill from index 0
    if (digits.length > 1) {
      const newOtp = [...Array(OTP_LENGTH).fill("")] as string[];
      digits
        .slice(0, OTP_LENGTH)
        .split("")
        .forEach((d: string, i: number) => {
          newOtp[i] = d;
        });
      setOtp(newOtp);
      setError(null);
      const focusIndex = Math.min(digits.length, OTP_LENGTH - 1);
      setTimeout(() => inputRefs.current[focusIndex]?.focus(), 50);
      if (newOtp.every((d) => d !== "")) {
        setTimeout(() => submitOtp(newOtp), 500);
      }
      return;
    }

    const digit = digits.slice(-1);
    const newOtp = [...otp];
    newOtp[index] = digit;
    setOtp(newOtp);
    setError(null);

    if (digit && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when all filled
    if (digit && newOtp.every((d) => d !== "")) {
      submitOtp(newOtp);
    }
  };

  const handleKeyDown = (e: any, index: number) => {
    if (e.nativeEvent.key === "Backspace") {
      if (!otp[index] && index > 0) {
        const newOtp = [...otp];
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.nativeEvent.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.nativeEvent.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: any) => {
    // Web: clipboardData; native: nativeEvent.text may not have paste
    const pastedText = e?.clipboardData?.getData?.("text") || e?.nativeEvent?.text || "";
    if (!pastedText) return;

    e.preventDefault?.();

    const digits = pastedText.replace(/\D/g, "").slice(0, OTP_LENGTH).split("");
    if (digits.length === 0) return;

    const newOtp = [...Array(OTP_LENGTH).fill("")];
    digits.forEach((d: string, i: number) => {
      newOtp[i] = d;
    });
    setOtp(newOtp);
    setError(null);

    // Focus the box after the last filled digit, or last box
    const focusIndex = Math.min(digits.length, OTP_LENGTH - 1);
    setTimeout(() => inputRefs.current[focusIndex]?.focus(), 50);

    // Auto-submit if all 6 filled (brief delay so user sees digits fill in)
    if (newOtp.every((d) => d !== "")) {
      setTimeout(() => submitOtp(newOtp), 500);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || !email) return;
    setResendCooldown(RESEND_COOLDOWN);
    setError(null);

    try {
      const redirectTo = `${window.location.origin}/auth/callback`;
      await supabase.auth.resetPasswordForEmail(email.trim(), { redirectTo });
    } catch {
      // Silently fail — user can try again after cooldown
    }
  };

  const handleVerify = () => {
    submitOtp(otp);
  };

  const allFilled = otp.every((d) => d !== "");

  if (!email) return null;

  return (
    <>
      <Head>
        <title>RockForce - Verify Code</title>
        <meta name="description" content="" />
      </Head>
      <View style={styles.outerContainer}>
        <KeyboardAvoidingView
          behavior={Platform.OS === "ios" ? "padding" : "height"}
          keyboardVerticalOffset={Platform.OS === "ios" ? 150 : 0}
          style={styles.keyboardView}
        >
          {/* Logo Section */}
          <View style={styles.logoSection}>
            <Image source={[logo]} style={styles.logo} />
            <ThemedText style={[styles.logoTitle, { color: colors.text }]}>RockForce</ThemedText>
          </View>

          {/* OTP Card */}
          <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
            {/* Header Icon */}
            <View style={[styles.headerIconContainer, { backgroundColor: colors.primary + "15" }]}>
              <MaterialIcons name="pin" size={40} color={colors.primary} />
            </View>

            <ThemedText style={[styles.cardTitle, { color: colors.text }]}>Enter Verification Code</ThemedText>
            <ThemedText style={[styles.cardSubtitle, { color: colors.textSecondary }]}>
              We sent a 6-digit code to {email}
            </ThemedText>

            {/* OTP Inputs */}
            <View style={styles.otpContainer}>
              {otp.map((digit, index) => (
                <TextInput
                  key={index}
                  ref={(ref) => {
                    inputRefs.current[index] = ref;
                  }}
                  style={[
                    styles.otpInput,
                    {
                      borderColor: digit ? colors.primary : error ? colors.dangerText || "#ef4444" : colors.border,
                      backgroundColor: colors.backgroundSecondary || colors.card,
                      color: colors.text,
                    },
                  ]}
                  value={digit}
                  onChangeText={(text) => handleChange(text, index)}
                  onKeyPress={(e) => handleKeyDown(e, index)}
                  // @ts-ignore — onPaste works on web
                  onPaste={handlePaste}
                  inputMode="numeric"
                  selectTextOnFocus
                  autoComplete="off"
                  // @ts-ignore — web-only attrs to suppress browser autofill
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
              ))}
            </View>

            {/* Error */}
            {error && (
              <View style={styles.errorContainer}>
                <MaterialIcons name="error-outline" size={18} color={colors.dangerText || "#ef4444"} />
                <ThemedText style={[styles.errorText, { color: colors.dangerText || "#ef4444" }]}>{error}</ThemedText>
              </View>
            )}

            {/* Verify Button */}
            {verifying ? (
              <ActivityIndicator size="large" color={colors.primary} style={styles.loader} />
            ) : (
              <Button
                text="Verify Code"
                onPress={handleVerify}
                icon="check-circle"
                iconPosition="right"
                iconSize={18}
                iconColor="white"
                enabled={allFilled}
                style={[
                  styles.primaryButton,
                  {
                    backgroundColor: allFilled ? colors.primary : colors.border,
                    opacity: allFilled ? 1 : 0.6,
                  },
                ]}
                textStyle={styles.primaryButtonText}
              />
            )}

            {/* Resend */}
            <Pressable style={styles.resendLink} onPress={handleResend} disabled={resendCooldown > 0}>
              <ThemedText style={[styles.resendText, { color: colors.textSecondary }]}>
                Didn&apos;t receive the code?{" "}
                {resendCooldown > 0 ? (
                  <ThemedText style={{ color: colors.textSecondary }}>Resend in {resendCooldown}s</ThemedText>
                ) : (
                  <ThemedText style={{ color: colors.primary, fontWeight: "600" }}>Resend Code</ThemedText>
                )}
              </ThemedText>
            </Pressable>

            {/* Back to Sign In */}
            <Pressable
              style={[styles.backButton, { borderColor: colors.border }]}
              onPress={() => router.replace("/sign-in")}
            >
              <MaterialIcons name="arrow-back" size={18} color={colors.textSecondary} />
              <ThemedText style={[styles.backButtonText, { color: colors.textSecondary }]}>Back to Sign In</ThemedText>
            </Pressable>
          </View>
        </KeyboardAvoidingView>
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  outerContainer: {
    flex: 1,
    alignItems: "center",
  },
  keyboardView: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
    width: "100%",
    maxWidth: 400,
  },
  logoSection: {
    alignItems: "center",
    marginBottom: 32,
  },
  logo: {
    height: 64,
    width: 64,
    marginBottom: 12,
  },
  logoTitle: {
    fontSize: 28,
    fontWeight: "800",
    letterSpacing: 2,
  },
  card: {
    width: "100%",
    borderRadius: 16,
    borderWidth: 1,
    padding: 24,
    alignItems: "center",
  },
  headerIconContainer: {
    width: 80,
    height: 80,
    borderRadius: 40,
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 20,
  },
  cardTitle: {
    fontSize: 22,
    fontWeight: "bold",
    marginBottom: 8,
  },
  cardSubtitle: {
    fontSize: 14,
    textAlign: "center",
    marginBottom: 24,
    lineHeight: 20,
  },
  otpContainer: {
    flexDirection: "row",
    gap: 8,
    marginBottom: 20,
  },
  otpInput: {
    width: 48,
    height: 56,
    borderWidth: 2,
    borderRadius: 10,
    textAlign: "center",
    fontSize: 24,
    fontWeight: "bold",
  },
  errorContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    marginBottom: 16,
  },
  errorText: {
    fontSize: 14,
  },
  loader: {
    marginVertical: 8,
  },
  primaryButton: {
    borderRadius: 10,
    minHeight: 50,
    width: "100%",
  },
  primaryButtonText: {
    color: "white",
    fontSize: 17,
    fontWeight: "600",
  },
  resendLink: {
    marginTop: 16,
  },
  resendText: {
    fontSize: 14,
    textAlign: "center",
  },
  backButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    marginTop: 20,
    paddingVertical: 12,
    borderTopWidth: 1,
    width: "100%",
  },
  backButtonText: {
    fontSize: 15,
    fontWeight: "500",
  },
});
